import type { Notice } from '~/pages/authentication/components/AuthNotice'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { MAIN_ID } from '~/components/SkipLink'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Label } from '~/components/ui/label'
import { PasswordInput } from '~/components/ui/password-input'
import { GQL_RESET_PASSWORD } from '~/gql/auth/auth'
import { passwordRule } from '~/helpers/auth/password-rule'
import AuthNotice from '~/pages/authentication/components/AuthNotice'

function NewPassword() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [notice, setNotice] = useState<Notice | null>(null)
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!token || !email) {
      navigate('/')
    }
  }, [token, email, navigate])

  const resetSchema = z.object({
    password: passwordRule(t('password.tooShort')),
    confirmPassword: z.string(),
  }).refine(data => data.password === data.confirmPassword, {
    message: t('password.not-match.message'),
    path: ['confirmPassword'],
  })

  type ResetFormValues = z.infer<typeof resetSchema>

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const [resetPassword, { loading }] = useMutation(GQL_RESET_PASSWORD)

  const onSubmit = async (values: ResetFormValues) => {
    if (!token || !email) return

    const tokenString = token as string
    const emailString = email as string

    try {
      const { data } = await resetPassword({
        variables: {
          email: emailString,
          token: tokenString,
          password: values.password,
        },
      })
      if (data && data.resetPassword) {
        setNotice({ kind: 'success', text: t('new.password.success') })
      } else {
        setNotice({ kind: 'error', text: t('bad.reset.message') })
      }
    } catch {
      setNotice({ kind: 'error', text: t('bad.server-login.message') })
    }
  }

  if (!token || !email) {
    return null
  }

  return (
    <main id={MAIN_ID} className="flex w-full min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">{t('new.password')}</CardTitle>
            <CardDescription>{t('new.password.description')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <AuthNotice notice={notice} />
              <RequiredFieldsHint />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <div className="grid gap-2">
                      <Label htmlFor="password" required>{t('common.password')}</Label>
                      <FormControl>
                        <PasswordInput
                          id="password"
                          aria-required
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <div className="grid gap-2">
                      <Label htmlFor="confirmPassword" required>{t('common.confirm-password')}</Label>
                      <FormControl>
                        <PasswordInput
                          id="confirmPassword"
                          aria-required
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Une fois le mot de passe changé, le jeton est consommé : un second envoi échouerait
                  et effacerait la confirmation. */}
              <Button type="submit" disabled={loading || notice?.kind === 'success'} className="w-full">
                {loading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('common.submit')}
              </Button>

              <div className="mt-4 text-center text-sm">
                <Link to="/" className="underline underline-offset-4">
                  {t('login.return-message')}
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  )
}

export default NewPassword
